// src/components/features/NewDescriptionForm.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Loader2, Sparkles, PlusCircle, XCircle, Link2, Brain, PackageCheck, HelpCircle,
  ListChecks, Palette, Video, Search, RefreshCw, MousePointerClick, X,
  Copy, Code2, Download, Save, Eye, Edit, CheckCircle, Wand2
} from 'lucide-react';


import { useStandards } from '../../hooks/useStandards';
import { SUBJECT_CONFIG, DESCRIPTION_TONES, DESCRIPTION_LENGTHS_NEW, UNIQUE_SORTED_RESOURCE_COMPONENTS, PEDAGOGICAL_APPROACHES } from '../../config/constants';
import { functionsInstance, db } from '../../firebase/firebase';
import { extractProductNameFromUrl } from '../../utils/helpers';

import SectionTitle from '../ui/SectionTitle';
import GradeLevelCheckboxGroup from '../forms/GradeLevelCheckboxGroup';
import DomainCheckboxGroup from '../forms/DomainCheckboxGroup';
import StandardsSelectionSection from '../forms/StandardsSelectionSection';

import { STANDARDS_BY_SUBJECT } from '../../config/standards';

// --- Enhanced MultiSelectDropdown Component (Added from Rewrite Form) ---
const MultiSelectDropdown = ({ options, selectedValues, onChange, buttonLabelPrefix, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) {
      return options;
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const displayLabel = useMemo(() => {
    if (selectedValues.length === 0) {
      return placeholder || `Select ${buttonLabelPrefix}...`;
    }
    // Just show count if many items, or list them if few
    if (selectedValues.length > 2) {
        return `${selectedValues.length} products selected`;
    }
    const selectedLabels = options
      .filter(option => selectedValues.includes(option.value))
      .map(option => option.label);
    return selectedLabels.join(', ');
  }, [selectedValues, options, buttonLabelPrefix, placeholder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (value) => {
    const newSelection = selectedValues.includes(value)
      ? selectedValues.filter(item => item !== value)
      : [...selectedValues, value];
    onChange(newSelection);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="w-full p-3 border border-slate-300 rounded-lg bg-white text-left shadow-sm focus:ring-2 focus:ring-sky-500 flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate pr-2">{displayLabel}</span>
        <span className="ml-2 flex-shrink-0">▼</span>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full bg-white border border-slate-300 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
          <div className="p-2 border-b border-slate-200 sticky top-0 bg-white">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search products..."
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          {filteredOptions.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">No results found.</div>
          ) : (
            <ul>
              {filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 flex items-center"
                  onClick={() => handleToggleOption(option.value)}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    readOnly
                    className="mr-2 h-4 w-4 text-sky-600 border-gray-300 rounded"
                  />
                  <span className="truncate">{option.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// --- Helper Functions ---
const resolveStandardGradeKeys = (subject, uiGrade) => {
  const subjectStandards = STANDARDS_BY_SUBJECT[subject];
  if (!subjectStandards) return [];

  const hasKey = (k) => Object.prototype.hasOwnProperty.call(subjectStandards, k);
  const coalesce = (band) => {
    const variants = [band, band.replace('–', '-'), band.replace('-', '–')];
    for (const v of variants) if (hasKey(v)) return v;
    return null;
  };

  if (hasKey(uiGrade)) return [uiGrade];

  const keys = Object.keys(subjectStandards);
  const hasHSSplit = keys.includes('High School (9-10)') || keys.includes('High School (11-12)');
  const hasBands = ['K-2','K–2','3-5','3–5','6-8','6–8','K-12','K–12'].some(hasKey);

  const uiIsHS = [
    'High School', 'High School (General 9-12)',
    '9th Grade', '10th Grade', '11th Grade', '12th Grade'
  ].includes(uiGrade);

  if (uiIsHS) {
    if (hasHSSplit) {
      if (['9th Grade', '10th Grade'].includes(uiGrade)) {
        return keys.includes('High School (9-10)') ? ['High School (9-10)'] : [];
      }
      if (['11th Grade', '12th Grade'].includes(uiGrade)) {
        return keys.includes('High School (11-12)') ? ['High School (11-12)'] : [];
      }
      return ['High School (9-10)', 'High School (11-12)'].filter(k => keys.includes(k));
    }
    if (keys.includes('High School')) return ['High School'];
  }

  if (hasBands) {
    const k2  = coalesce('K-2');
    const g35 = coalesce('3-5');
    const g68 = coalesce('6-8');

    if (['Pre-K','Kindergarten','1st Grade','2nd Grade'].includes(uiGrade) && k2) return [k2];
    if (['3rd Grade','4th Grade','5th Grade'].includes(uiGrade) && g35) return [g35];
    if (['6th Grade','7th Grade','8th Grade'].includes(uiGrade) && g68) return [g68];
  }

  if (uiGrade === 'Functional Skills (All Levels)' && hasKey('Functional Skills (All Levels)')) {
    return ['Functional Skills (All Levels)'];
  }
  if (['Not Grade Specific','All Grades'].includes(uiGrade)) {
    if (hasKey('Not Grade Specific')) return ['Not Grade Specific'];
    const k12 = coalesce('K-12');
    if (k12) return [k12];
  }
  return [];
};

const buildRefinePrompt = ({ currentTitle, currentDescription, rephraseRequest }) => {
  const systemPrompt = `You are an expert TPT copywriter. Your task is to edit and refine an existing HTML description based on a user's request.
You MUST maintain the exact HTML structure (p, h3, ul, li, strong, a).
Only change the text content to meet the user's request.
Return *only* the new, full HTML description.`;

  const userPrompt = `
**USER REQUEST:**
"${rephraseRequest}"

**CURRENT PRODUCT TITLE:**
"${currentTitle}"

**CURRENT HTML DESCRIPTION (to refine):**
${currentDescription}

**YOUR TASK:**
Rewrite the full HTML description above to incorporate the user's request.
Remember to maintain the HTML structure and do not add any commentary.
`.trim();

  return { systemPrompt, userPrompt };
};


const NewDescriptionForm = ({
  currentUser,
  triggerAuthModal,
  onDescriptionGenerated,
  onFormStateChange,
  userTptProducts,
  snapshotToLoad,
  productName,
  setProductName,
  onDescriptionSaved
}) => {
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'bullet' }, { 'list': 'ordered'}],
      ['link']
    ],
  };

  // State
  const [gradeLevels, setGradeLevels] = useState([]);
  const [primarySubject, setPrimarySubject] = useState('');
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [selectedPedagogicalApproaches, setSelectedPedagogicalApproaches] = useState([]);
  const [selectedIncludedItems, setSelectedIncludedItems] = useState([]);
  const [customIncludedItem, setCustomIncludedItem] = useState('');
  const [relatedProducts, setRelatedProducts] = useState([{ id: Date.now(), url: '' }]);
  const [suggestLearningOutcomes, setSuggestLearningOutcomes] = useState(true);
  const [customKeywords, setCustomKeywords] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [tone, setTone] = useState(DESCRIPTION_TONES[0]);
  const [descriptionLength, setDescriptionLength] = useState(DESCRIPTION_LENGTHS_NEW[1].value);
  const [selectedStandardCodes, setSelectedStandardCodes] = useState([]);

  // Generation & Output State
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [refineRequest, setRefineRequest] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');

  // UI State
  const [showInteractiveModal, setShowInteractiveModal] = useState(false);
  const [viewMode, setViewMode] = useState('preview');
  const [copySuccess, setCopySuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const formRef = useRef(null);
  const outputSectionRef = useRef(null);

  const { filteredStandards, isLoading: standardsAreLoading, error: standardsError } =
    useStandards(primarySubject, gradeLevels, selectedDomains);

  const subjectHasStandards = useMemo(() => {
    const map = STANDARDS_BY_SUBJECT[primarySubject];
    return !!(map && Object.keys(map).length);
  }, [primarySubject]);

  const availableDomains = useMemo(() => {
    if (!primarySubject || gradeLevels.length === 0) return [];
    const subjectStandards = STANDARDS_BY_SUBJECT[primarySubject];
    console.log('[availableDomains] subject:', primarySubject, '| found in STANDARDS_BY_SUBJECT?', !!subjectStandards, '| first 3 keys:', subjectStandards ? Object.keys(subjectStandards).slice(0, 3) : 'N/A');
    if (!subjectStandards) return [];
    const domains = new Set();
    for (const g of gradeLevels) {
      const keys = resolveStandardGradeKeys(primarySubject, g);
      for (const k of keys) {
        const block = subjectStandards[k];
        if (!block) continue;
        Object.keys(block).forEach(d => domains.add(d));
      }
    }
    return Array.from(domains);
  }, [primarySubject, gradeLevels]);

  // Snapshot loader
  useEffect(() => {
    if (snapshotToLoad) {
      setProductName(snapshotToLoad.productName || '');
      setGradeLevels(snapshotToLoad.gradeLevels || []);
      setPrimarySubject(snapshotToLoad.primarySubject || '');
      setSelectedDomains(snapshotToLoad.selectedDomains || []);
      setSelectedPedagogicalApproaches(snapshotToLoad.selectedPedagogicalApproaches || []);
      setSelectedIncludedItems(snapshotToLoad.selectedIncludedItems || []);
      setCustomIncludedItem(snapshotToLoad.customIncludedItem || '');
      setRelatedProducts(
        snapshotToLoad.relatedProducts?.length > 0
          ? snapshotToLoad.relatedProducts.map(rp => ({ ...rp, id: rp.id || Date.now() + Math.random() }))
          : [{ id: Date.now(), url: '' }]
      );
      setSuggestLearningOutcomes(snapshotToLoad.suggestLearningOutcomes ?? true);
      setCustomKeywords(snapshotToLoad.customKeywords || '');
      setAdditionalContext(snapshotToLoad.additionalContext || '');
      setTargetAudience(snapshotToLoad.targetAudience || '');
      setTone(snapshotToLoad.tone || DESCRIPTION_TONES[0]);
      setDescriptionLength(snapshotToLoad.descriptionLength || DESCRIPTION_LENGTHS_NEW[1].value);
      setSelectedStandardCodes(snapshotToLoad.selectedStandardCodes || []);
      setRefineRequest('');

      if (snapshotToLoad.generatedDescription) {
        setGeneratedContent(snapshotToLoad.generatedDescription);

        setTimeout(() => {
            if (outputSectionRef.current) {
                outputSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
      } else {
         setGeneratedContent('');
      }
    }
  }, [snapshotToLoad, setProductName]);

  // Notify parent of form changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange({
        productName, gradeLevels, primarySubject, tone,
        selectedPedagogicalApproaches, selectedIncludedItems, customIncludedItem
      });
    }
  }, [
    productName, gradeLevels, primarySubject, tone, selectedPedagogicalApproaches,
    selectedIncludedItems, customIncludedItem, onFormStateChange
  ]);

  useEffect(() => {
    const availableIds = new Set(filteredStandards.map(s => s.id));
    setSelectedStandardCodes(prev => prev.filter(id => availableIds.has(id)));
  }, [filteredStandards]);

  useEffect(() => {
    setSelectedDomains(prev => prev.filter(d => availableDomains.includes(d)));
  }, [availableDomains]);

  useEffect(() => {
    if (!subjectHasStandards) {
      setSelectedDomains([]);
      setSelectedStandardCodes([]);
    }
  }, [subjectHasStandards]);

  const handleCheckboxChange = (setter, value, currentValues) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value];
    setter(newValues);
  };

  const generateDescriptionPromptString = () => {
    const selectedStandardsDetails = selectedStandardCodes
      .map(id => filteredStandards.find(s => s.id === id))
      .filter(Boolean);

    const promptData = `
---
**PRODUCT DATA:**
- Product Name: "${productName || 'A new resource'}"
- Grade Level(s): "${gradeLevels.join(', ')}"
- Primary Subject: "${primarySubject}"
- Resource Components: "${[...selectedIncludedItems, ...customIncludedItem.trim().split(',')].filter(i => i).join(', ')}"
- Key Pedagogical Approaches: "${selectedPedagogicalApproaches.join(', ')}"
- Aligned Standards: "${selectedStandardsDetails.map(s => `${s.code}: ${s.description}`).join('; ')}"
- Target Audience: "${targetAudience}"
- Custom Keywords: "${customKeywords}"
- Additional Context: "${additionalContext}"
---
`;

    let promptInstructions = `
**YOUR TASK:**
You are an expert TPT copywriter and SEO specialist. Your goal is to create a compelling, easy-to-read, and highly optimized product description that will rank high in TPT search results.
Your tone should be **${tone}**.
The final output must be well-structured HTML. Use <h3> for main headings with emojis, <strong> for emphasis, and <ul> with <li> for lists.

**CRITICAL FORMATTING RULE:**
For all paragraphs (like the Hook, Introduction, and Narrative), you MUST use multiple <p> tags to break up thoughts. Each <p> tag should contain only 2-3 sentences maximum to prevent long "walls of text".

**SEO STRATEGY:**
For optimal SEO, naturally weave keywords throughout the text.
- Ensure the most important keywords from the PRODUCT DATA (like Grade Level, Subject, and key topics from the Product Name) appear in the headings and the opening hook/introduction paragraphs.
- The "Perfect For..." section is crucial for capturing long-tail keyword searches (e.g., "math intervention activities," "emergency sub plans"), so make it comprehensive.
- Use <strong> tags on important keyword phrases to make them stand out to both teachers and search engines.

**TEMPLATE TO FILL (in this logical order):**

<p>[HOOK PARAGRAPH]
Write a 1-2 sentence compelling hook that addresses a common problem for a teacher of ${gradeLevels.join(', ')} ${primarySubject}.

<p>[INTRODUCTION PARAGRAPH]
Write a passionate paragraph that introduces the product, "${productName}", as the perfect solution. Mention the key benefit for the teacher (e.g., saves time, boosts engagement). Adhere to the specified length for this paragraph: **${descriptionLength}**.

<h3>✨ What's Included:</h3>
<ul>
  <li>[List 1-3 key components from "Resource Components" data, synthesizing from the old description]</li>
  <li>[List 1-3 more key components]</li>
  <li>...</li>
</ul>

<h3>✅ Perfect For...</h3>
<ul>
  <li>[List 2-3 teacher scenarios, e.g., "Intervention groups," "Homeschooling," "Test prep"]</li>
</ul>

[NARRATIVE PARAGRAPH]
Write a passionate paragraph about the teaching philosophy behind this resource. Use the "Key Pedagogical Approaches" data to explain *how* this resource transforms the learning experience and helps students succeed.
`;

    if (selectedStandardCodes.length > 0) {
      promptInstructions += `
<h3>🎯 Standards Alignment</h3>
<ul>
    </ul>
`;
    }

    if (suggestLearningOutcomes) {
      promptInstructions += `
<h3>📚 Learning Objectives</h3>
<ul>
    </ul>
`;
    }

    const validRelatedProducts = relatedProducts
      .map(rp => ({ ...rp, name: extractProductNameFromUrl(rp.url) || "Related Product" }))
      .filter(rp => rp.url && rp.url.trim());

    if (validRelatedProducts.length > 0) {
      const relatedProductsHTML = validRelatedProducts.map(rp => `<li><a href="${rp.url}">${rp.name}</a></li>`).join('');
      promptInstructions += `
<h3>You Might Also Like...</h3>
<ul>
    ${relatedProductsHTML}
</ul>
`;
    }

    promptInstructions += `
[FINAL CLOSING PARAGRAPH & CALL TO ACTION]
Write a final, enthusiastic 1-2 sentence paragraph, followed by a strong, direct call to action like "Click <strong>Add to Cart</strong> and bring a new level of engagement to your classroom today!"

**FINAL INSTRUCTION:**
Proofread the entire generated description for perfect grammar, spelling, and consistent case before providing the final output.
`;
    return promptInstructions + promptData;
  };

  const handleSubmitDescription = async (e) => {
    e.preventDefault();
    const analytics = getAnalytics();
    if (!currentUser) {
      triggerAuthModal("Please sign up for a free trial to generate descriptions.", 'signup');
      return;
    }
    if (!productName) {
      setError("Please fill in at least the Product Name.");
      return;
    }
    setError('');
    setIsGenerating(true);
    setRefineRequest('');
    setGeneratedContent('');

    const promptToSend = generateDescriptionPromptString();
    const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');

    try {
      const result = await callGeminiCloudFunction({ prompt: promptToSend, model: "gemini-2.5-flash-lite" });
      const geminiResponseText = result.data?.generatedText;

      if (geminiResponseText) {
        const text = geminiResponseText.trim();
        setGeneratedContent(text);
        logEvent(analytics, 'generate_description', { length: descriptionLength, tone: tone });

        if (outputSectionRef.current) {
            outputSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        setError("Failed to generate description: Empty response from AI.");
      }
    } catch (submitError) {
      console.error("Firebase Function error details:", submitError);
      setError(`An error occurred. Check the browser console and Firebase logs for details. (Code: ${submitError.code}, Message: ${submitError.message})`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRefineDescription = async (e) => {
    e.preventDefault();
    const analytics = getAnalytics();
    if (!currentUser) {
      triggerAuthModal("Please sign up for a free trial.", 'signup');
      return;
    }
    if (!refineRequest.trim()) {
      setError("Please enter a refinement request.");
      return;
    }
    if (!generatedContent) {
      setError("Please generate a description first before refining.");
      return;
    }

    setError('');
    setIsGenerating(true);

    try {
      const { systemPrompt, userPrompt } = buildRefinePrompt({
        currentTitle: productName,
        currentDescription: generatedContent,
        rephraseRequest: refineRequest,
      });

      const fullPrompt = `${systemPrompt}\n\n${userPrompt}`;
      const callGeminiCloudFunction = httpsCallable(functionsInstance, 'callGeminiAPI');

      const result = await callGeminiCloudFunction({
        prompt: fullPrompt,
        model: "gemini-2.5-flash-lite"
      });

      const text = result.data?.generatedText;
      if (!text) throw new Error("Received an empty response from the AI.");

      setGeneratedContent(text.trim());
      setRefineRequest('');
      logEvent(analytics, 'refine_description');

      if (outputSectionRef.current) {
          outputSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

    } catch (e) {
      console.error("Error refining description:", e);
      setError(`Error refining description: ${e.message || 'An unknown error occurred.'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * UPDATED: copyToClipboard
   * Handles both Raw HTML copy and Visual (Rich Text) copy
   * FIX: Injects <br> tags between paragraphs/lists AND HEADERS (h3) to force TPT to display spacing
   */
  const copyToClipboard = async (textToCopy, type, asHtml = false) => {
    if (!textToCopy) return;

    try {
      // --- TPT SPACING FIX ---
      // TPT strips standard CSS margins from <p> and <h3> tags. We must physically inject
      // a <br> tag between these elements to force a visual gap in their editor.

      let formattedContent = textToCopy
        .replace(/<\/p>\s*<p>/g, '</p><br><p>')      // Space between Paragraphs
        .replace(/<\/ul>\s*<p>/g, '</ul><br><p>')    // Space between List and Paragraph
        .replace(/<\/p>\s*<h3>/g, '</p><br><h3>')    // Space between Paragraph and Header (The Emoji Sections)
        .replace(/<\/ul>\s*<h3>/g, '</ul><br><h3>'); // Space between List and Header (The Emoji Sections)
      // -----------------------

      if (asHtml) {
        // Copy Raw HTML (Source Code) - Uses the fixed content
        await navigator.clipboard.writeText(formattedContent);
      } else {
        // Copy Rendered Text (Visual)
        // We use the fixed content here too, so if they paste into a visual editor
        // that strips margins, the <br> is still there as a backup.
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formattedContent;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        const successful = document.execCommand('copy');
        document.body.removeChild(tempDiv);
        selection.removeAllRanges();
        if (!successful) throw new Error('Copy command failed');
      }
      setCopySuccess(asHtml ? 'Raw HTML copied (TPT Optimized)!' : 'Formatted text copied!');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setCopySuccess('Failed to copy. Please try manually.');
    }
  };

  const handleDownloadHtml = (content) => {
    if (!content) return;
    const filename = `${(productName || 'generated_description').trim().replace(/\s+/g, '_')}.html`;
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleSaveDescription = async () => {
    if (!currentUser) return;
    setIsSaving(true);
    setError('');

    try {
      const isUpdatingExisting = snapshotToLoad && snapshotToLoad.id;

      const dataToSave = {
        saveName: productName,
        productName,
        gradeLevels,
        primarySubject,
        selectedDomains,
        selectedIncludedItems,
        customIncludedItem,
        tone,
        descriptionLength,
        selectedPedagogicalApproaches,
        customKeywords,
        additionalContext,
        targetAudience,
        suggestLearningOutcomes,
        relatedProducts,
        generatedDescription: generatedContent,
        lastUpdated: serverTimestamp(),
      };

      let actionTaken = 'created';
      let docId = null;

      if (isUpdatingExisting) {
        const docRef = doc(db, 'users', currentUser.uid, 'savedDescriptions', snapshotToLoad.id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          await updateDoc(docRef, dataToSave);
          actionTaken = 'updated';
          docId = snapshotToLoad.id;
        } else {
          console.warn("Loaded document ID is stale/missing. Saving as a new draft.");
        }
      }

      if (actionTaken === 'created') {
        const dataToCreate = {
            ...dataToSave,
            savedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
        };
        const colRef = collection(db, 'users', currentUser.uid, 'savedDescriptions');
        const docRef = await addDoc(colRef, dataToCreate);
        docId = docRef.id;
      }

      setCopySuccess(`Draft successfully ${actionTaken}!`);
      setTimeout(() => setCopySuccess(''), 3000);

      if (onDescriptionSaved && docId) {
        onDescriptionSaved({
          id: docId,
          ...dataToSave,
          savedAt: isUpdatingExisting ? snapshotToLoad.savedAt : new Date(),
          lastUpdated: new Date()
        });
      }

    } catch (err) {
      console.error("Error saving:", err);
      setError("Failed to save description. Please check the console for details.");
    } finally {
      setIsSaving(false);
    }
  };


  const addRelatedProductField = () => setRelatedProducts(prev => [...prev, { id: Date.now(), url: '' }]);
  const handleRelatedProductUrlChange = (id, value) => setRelatedProducts(prev => prev.map(rp => (rp.id === id ? { ...rp, url: value } : rp)));
  const removeRelatedProductField = (id) => setRelatedProducts(prev => prev.filter(rp => rp.id !== id));

  // Logic to handle selection from MultiSelectDropdown
  const handleRelatedMultiSelect = (selectedUrls) => {
    setRelatedProducts(prev => {
      // 1. Keep manual items (URLs not in userTptProducts list)
      const manualItems = prev.filter(item => {
        // If the item's URL is empty, keep it (it's a new blank field)
        if (!item.url) return true;
        // If the URL is NOT found in the user's TPT product list, keep it (it's a manual external link)
        return !userTptProducts.some(p => p.tptUrl === item.url);
      });

      // 2. Add items that are currently selected in the dropdown
      const selectedItems = selectedUrls.map(url => {
        // Check if this URL already existed in the previous state to preserve its ID
        const existingItem = prev.find(p => p.url === url);
        if (existingItem) return existingItem;
        return { id: Date.now() + Math.random(), url };
      });

      // 3. Combine them. (Filter out empty manual items if we have valid selected items to avoid clutter, optional)
      let combined = [...manualItems, ...selectedItems];

      // If we have no items at all, ensure one empty field remains
      if (combined.length === 0) {
        combined = [{ id: Date.now(), url: '' }];
      } else if (combined.length > 1) {
         // Clean up empty fields if we have real data now
         const hasRealData = combined.some(i => i.url.trim() !== '');
         if(hasRealData) {
             combined = combined.filter(i => i.url.trim() !== '');
         }
      }

      return combined;
    });
  };

  return (
    <form ref={formRef} onSubmit={handleSubmitDescription} className="font-sans relative bg-white p-6 sm:p-8 rounded-xl shadow-2xl space-y-6 mb-8">
      {/* --- BUTTONS CONTAINER (Interactive Walkthrough) --- */}
      <div className="absolute top-6 right-6 flex items-center gap-2">
        <button
            type="button"
            onClick={() => {
                setShowInteractiveModal(true);
                try {
                   const analytics = getAnalytics();
                   logEvent(analytics, 'guide_click', {
                        content_type: 'interactive_walkthrough',
                        item_id: 'new_description_generator_interactive'
                    });
                } catch(e) { /* silent fail */ }
            }}
            className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
        >
            <MousePointerClick size={14} />
            Interactive Walkthrough
        </button>

        <a
            href="https://youtu.be/q2GyjPQVb28?si=G-vJQL5BZ_b7Bppf&t=179"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
        >
            <Video size={14} />
            Watch Tutorial
        </a>
      </div>

      <SectionTitle icon={ListChecks}>Generate New Product Description Details</SectionTitle>
      <p className="text-xs text-slate-500 -mt-3 mb-3 italic">
        Fill in as much detail as possible. The more context you give the AI, the better your description will be.
      </p>

      <div>
        <label htmlFor="productName" className="block text-sm font-medium text-slate-700 mb-1">
          Product Name / Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="productName"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          placeholder="e.g., Ultimate Kindergarten Math Bundle"
          className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
          required
        />
        <p className="text-xs text-slate-500 mt-1 italic">
          Tip: You can use the Title Generator above to get ideas and auto-fill this field.
        </p>
      </div>

      <SectionTitle icon={PackageCheck}>Resource Components & What's Included</SectionTitle>
      <p className="text-xs text-slate-500 -mt-3 mb-3 italic">Select all the parts that make up your product.</p>
      <DomainCheckboxGroup
        title="Select all applicable components/types"
        options={UNIQUE_SORTED_RESOURCE_COMPONENTS}
        selectedValues={selectedIncludedItems}
        onChange={(item) => handleCheckboxChange(setSelectedIncludedItems, item, selectedIncludedItems)}
      />
      <div>
        <label htmlFor="customIncludedItem" className="block text-sm font-medium text-slate-700 mb-1">
          Other Specific Items (comma-separated, if not listed above)
        </label>
        <input
          type="text"
          id="customIncludedItem"
          value={customIncludedItem}
          onChange={(e) => setCustomIncludedItem(e.target.value)}
          placeholder="e.g., Interactive digital game, Printable craft templates"
          className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
        />
      </div>

      <GradeLevelCheckboxGroup
        selectedValues={gradeLevels}
        onChange={(gradeValue) => handleCheckboxChange(setGradeLevels, gradeValue, gradeLevels)}
      />

      <SectionTitle icon={HelpCircle}>Subject, Domain & Standards</SectionTitle>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="primarySubject" className="block text-sm font-medium text-slate-700 mb-1">
            Primary Subject <span className="text-red-500">*</span>
          </label>
          <select
            id="primarySubject"
            value={primarySubject}
            onChange={(e) => {
              setPrimarySubject(e.target.value);
              setSelectedDomains([]);
              setSelectedStandardCodes([]);
            }}
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 bg-white"
            required
          >
            <option value="">-- Select a Subject --</option>
            {Object.keys(SUBJECT_CONFIG).sort().map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <p className="text-xs text-slate-500 italic">
            {subjectHasStandards
              ? 'Select domains after choosing at least one grade.'
              : primarySubject
                ? 'Standards are not available for the selected subject yet.'
                : 'Choose a subject to see available domains.'}
          </p>
        </div>
      </div>

      {primarySubject && subjectHasStandards && (
        gradeLevels.length === 0 ? (
          <p className="text-xs text-slate-500 pl-1 italic">
            Select at least one grade level to choose domains.
          </p>
        ) : (
          <DomainCheckboxGroup
            title={`${primarySubject} Domain(s)/Topic(s)`}
            options={availableDomains}
            selectedValues={selectedDomains}
            onChange={(domain) => handleCheckboxChange(setSelectedDomains, domain, selectedDomains)}
          />
        )
      )}

      <div className="mt-4 min-h-[10rem]">
        {standardsAreLoading && (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            <span className="ml-3 text-slate-600">Loading Standards...</span>
          </div>
        )}
        {standardsError && (
          <div className="p-4 text-red-700 bg-red-100 rounded-lg">
            <p>Could not load standards. Please try another subject.</p>
          </div>
        )}
        {!standardsAreLoading && (
          <StandardsSelectionSection
            standards={filteredStandards}
            selectedIds={selectedStandardCodes}
            onSelectionChange={(id) => handleCheckboxChange(setSelectedStandardCodes, id, selectedStandardCodes)}
            primarySubject={primarySubject}
            gradeLevels={gradeLevels}
            selectedDomains={selectedDomains}
          />
        )}
      </div>

      <SectionTitle icon={Brain}>Pedagogical Features & Customization</SectionTitle>
      <DomainCheckboxGroup
        title="Pedagogical Approaches / Resource Features"
        options={PEDAGOGICAL_APPROACHES}
        selectedValues={selectedPedagogicalApproaches}
        onChange={(approach) => handleCheckboxChange(setSelectedPedagogicalApproaches, approach, selectedPedagogicalApproaches)}
      />
      <div className="flex items-center space-x-2 mt-4 p-2 bg-sky-50 rounded-md border border-sky-100">
        <input
          type="checkbox"
          id="suggestLearningOutcomes"
          checked={suggestLearningOutcomes}
          onChange={(e) => setSuggestLearningOutcomes(e.target.checked)}
          className="form-checkbox h-4 w-4 text-sky-600 border-slate-400 rounded focus:ring-sky-500"
        />
        <label htmlFor="suggestLearningOutcomes" className="text-sm font-medium text-slate-700">
          Suggest Learning Outcomes for me?
        </label>
      </div>

      <div>
        <label htmlFor="customKeywords" className="block text-sm font-medium text-slate-700 mb-1">Custom Keywords (comma-separated)</label>
        <input
          type="text"
          id="customKeywords"
          value={customKeywords}
          onChange={(e) => setCustomKeywords(e.target.value)}
          placeholder="e.g., common core aligned, no prep"
          className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
        />
      </div>

      <div>
        <label htmlFor="targetAudience" className="block text-sm font-medium text-slate-700 mb-1">Target Audience / Specific Niche (Optional)</label>
        <input
          type="text"
          id="targetAudience"
          value={targetAudience}
          onChange={(e) => setTargetAudience(e.target.value)}
          placeholder="e.g., new teachers, special education students"
          className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
        />
      </div>

      <div>
        <label htmlFor="additionalContext" className="block text-sm font-medium text-slate-700 mb-1">Additional Context / Unique Selling Points (Optional)</label>
        <textarea
          id="additionalContext"
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
          rows="3"
          placeholder="e.g., Includes differentiation tips"
          className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
        ></textarea>
      </div>

      <SectionTitle icon={Link2}>Related Products/Resources (Optional)</SectionTitle>
      <div className={`mb-3 ${!currentUser ? 'opacity-50 pointer-events-none' : ''}`}>
        <label className="block text-sm font-medium text-slate-700 mb-1">Select from My TPT Products:</label>

        {/* --- REPLACED <select> with <MultiSelectDropdown> --- */}
        <MultiSelectDropdown
          options={userTptProducts.map(p => ({ value: p.tptUrl, label: p.productTitle }))}
          selectedValues={relatedProducts.map(p => p.url).filter(url => userTptProducts.some(u => u.tptUrl === url))}
          onChange={handleRelatedMultiSelect}
          placeholder="-- Select products from your list --"
          buttonLabelPrefix="Products"
        />
        {/* --------------------------------------------------- */}

      </div>
      <div className="space-y-3">
        {relatedProducts.map((rp, index) => (
          <div key={rp.id} className="flex items-center space-x-2">
            <input
              type="url"
              value={rp.url}
              onChange={(e) => handleRelatedProductUrlChange(rp.id, e.target.value)}
              placeholder={`Product URL ${index + 1}`}
              className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
            />
            <button
              type="button"
              onClick={() => removeRelatedProductField(rp.id)}
              className="p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100"
              title="Remove URL"
            >
              <XCircle size={20} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRelatedProductField}
          className="text-sm text-sky-600 hover:text-sky-800 font-medium flex items-center"
        >
          <PlusCircle size={18} className="mr-1" /> Add Another Product Link
        </button>
      </div>

      <SectionTitle icon={Palette}>Final Style Options</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div>
          <label htmlFor="tone" className="block text-sm font-medium text-slate-700 mb-1">Description Tone</label>
          <select
            id="tone"
            value={tone}
            onChange={e => setTone(e.target.value)}
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 bg-white"
          >
            {DESCRIPTION_TONES.map(toneOption => <option key={toneOption} value={toneOption}>{toneOption}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="descriptionLength" className="block text-sm font-medium text-slate-700 mb-1">Main Description Length</label>
          <select
            id="descriptionLength"
            value={descriptionLength}
            onChange={e => setDescriptionLength(e.target.value)}
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 bg-white"
          >
            {DESCRIPTION_LENGTHS_NEW.map(len => <option key={len.value} value={len.value}>{len.label}</option>)}
          </select>
        </div>
      </div>

      <div className="pt-4 text-center">
        <button
          type="submit"
          disabled={isGenerating}
          className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 disabled:bg-slate-400 text-white font-semibold py-3 px-8 rounded-lg shadow-md flex items-center justify-center transition-colors"
        >
          {isGenerating ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Sparkles className="h-5 w-5 mr-2" />}
          Generate Product Description
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {generatedContent && (
        <div
          ref={outputSectionRef}
          className="mt-8 border-t-2 border-slate-200 pt-6 animate-fade-in"
        >
          <SectionTitle icon={Wand2}>Generated Product Description</SectionTitle>

          <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-3 gap-2">
            <div className="flex items-center rounded-lg bg-slate-200 p-0.5">
              <button type="button" onClick={() => setViewMode('preview')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${viewMode === 'preview' ? 'bg-white shadow' : 'bg-transparent'}`}><Eye size={16} className="mr-1 sm:mr-2" />Preview</button>
              <button type="button" onClick={() => setViewMode('html')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${viewMode === 'html' ? 'bg-white shadow' : 'bg-transparent'}`}><Code2 size={16} className="mr-1 sm:mr-2" />HTML</button>
              <button type="button" onClick={() => setViewMode('edit')} className={`text-xs sm:text-sm font-medium py-1 px-3 rounded-md flex items-center ${viewMode === 'edit' ? 'bg-white shadow' : 'bg-transparent'}`}><Edit size={16} className="mr-1 sm:mr-2" />Edit</button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
               <button type="button" onClick={handleSaveDescription} disabled={isSaving || !currentUser} className="flex items-center text-xs sm:text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium py-1.5 px-3 rounded-lg"><Save size={16} className="mr-1 sm:mr-2" /> Save This Description & Inputs</button>
               <button type="button" onClick={() => copyToClipboard(generatedContent, 'output', false)} className="flex items-center text-xs sm:text-sm bg-slate-600 hover:bg-slate-700 text-white font-medium py-1.5 px-3 rounded-lg"><Copy size={16} className="mr-1 sm:mr-2" /> Copy Text</button>
               <button type="button" onClick={() => copyToClipboard(generatedContent, 'output', true)} className="flex items-center text-xs sm:text-sm bg-slate-500 hover:bg-slate-600 text-white font-medium py-1.5 px-3 rounded-lg" title="Copy raw HTML for TPT"><Code2 size={16} className="mr-1 sm:mr-2" /> Copy HTML</button>
               <button type="button" onClick={() => handleDownloadHtml(generatedContent)} className="flex items-center text-xs sm:text-sm bg-slate-600 hover:bg-slate-700 text-white font-medium py-1.5 px-3 rounded-lg"><Download size={16} className="mr-1 sm:mr-2" /> Download .html</button>
            </div>
          </div>

          {copySuccess && (<div className="mb-2 p-2 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm flex items-center"><CheckCircle className="h-4 w-4 mr-2" />{copySuccess}</div>)}

          {viewMode === 'preview' && (
            <div className="prose max-w-none bg-sky-50 p-4 rounded-md border border-sky-200 overflow-y-auto max-h-[500px]" dangerouslySetInnerHTML={{ __html: generatedContent }} />
          )}
          {viewMode === 'edit' && (
            <ReactQuill theme="snow" value={generatedContent} onChange={setGeneratedContent} modules={quillModules} className="bg-white" />
          )}
          {viewMode === 'html' && (
            <textarea readOnly value={generatedContent} className="w-full p-3 border border-slate-300 rounded-lg font-mono text-xs bg-slate-50" rows={15}/>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-lg mt-6">
            <div className="p-4 space-y-2">
              <label className="text-sm font-medium text-slate-700 block">
                Refine Generated Description (Optional)
              </label>
              <p className="text-xs text-slate-500 italic">
                Type a command to rephrase the description you just generated.
              </p>
              <textarea
                className="w-full p-2 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
                rows="2"
                placeholder="e.g., Make it more exciting, focus on 6th grade, make the hook shorter..."
                value={refineRequest}
                onChange={(e) => setRefineRequest(e.target.value)}
              />
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium shadow-sm disabled:bg-slate-400"
                onClick={handleRefineDescription}
                disabled={isGenerating || !refineRequest}
              >
                {isGenerating ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                Refine
              </button>
            </div>
          </div>
        </div>
      )}

      {showInteractiveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowInteractiveModal(false)}
        >
          <div
            className="w-full max-w-7xl rounded-xl bg-slate-800 border border-slate-700 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Interactive Walkthrough
              </h3>
              <button
                type="button"
                onClick={() => setShowInteractiveModal(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            <div
                style={{
                    position: 'relative',
                    boxSizing: 'content-box',
                    maxHeight: '80vh',
                    width: '100%',
                    aspectRatio: '2.013986013986014',
                    padding: '40px 0 40px 0',
                    background: '#1e293b'
                }}
            >
                <iframe
                    src="https://app.supademo.com/embed/cmi5ope742tyzb7b4cwuav7rv?embed_v=2&utm_source=embed"
                    loading="lazy"
                    title="Mr Joel's Seller Tools: New Description Generator"
                    allow="clipboard-write"
                    frameBorder="0"
                    webkitallowfullscreen="true"
                    mozallowfullscreen="true"
                    allowFullScreen
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }}
                ></iframe>
            </div>
          </div>
        </div>
      )}

    </form>
  );
};

export default NewDescriptionForm;